import {
    GoogleAuthProvider,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { loginWithKakao, loginWithNaver } from "../utils/socialLogin";
import { DEFAULT_COLOR, normalizeThemeKey, resolveThemeColorClass } from "../constants/theme";

export function createSocialAuthHandlers({
    auth,
    db,
    openAlert,
    pendingSocialData,
    user,
    setUser,
    setUserProfile,
    setPendingSocialData,
    setAppReady,
    setLoading,
    seedDemoData
}) {
    const buildThemePayload = (additionalInfo = {}) => {
        const resolvedDeptThemeKey = normalizeThemeKey(additionalInfo.deptThemeKey || additionalInfo.deptColor || DEFAULT_COLOR);
        const resolvedDeptColor = resolveThemeColorClass(resolvedDeptThemeKey);

        const normalizedDepartmentThemeKeys = additionalInfo.departmentThemeKeys
            ? Object.fromEntries(
                Object.entries(additionalInfo.departmentThemeKeys).map(([dept, keyOrColor]) => [
                    dept,
                    normalizeThemeKey(keyOrColor)
                ])
            )
            : (additionalInfo.deptName ? { [additionalInfo.deptName]: resolvedDeptThemeKey } : {});

        const normalizedDepartmentColors = additionalInfo.departmentColors
            ? Object.fromEntries(
                Object.entries(additionalInfo.departmentColors).map(([dept, keyOrColor]) => [
                    dept,
                    resolveThemeColorClass(keyOrColor)
                ])
            )
            : Object.fromEntries(
                Object.entries(normalizedDepartmentThemeKeys).map(([dept, key]) => [
                    dept,
                    resolveThemeColorClass(key)
                ])
            );

        return {
            deptThemeKey: resolvedDeptThemeKey,
            deptColor: resolvedDeptColor,
            departmentThemeKeys: normalizedDepartmentThemeKeys,
            departmentColors: normalizedDepartmentColors
        };
    };

    const processSocialLogin = async (firebaseUser, socialProfile) => {
        try {
            const userRef = doc(db, "users", firebaseUser.uid);
            const docSnap = await getDoc(userRef);

            if (docSnap.exists() && docSnap.data().churchName) {
                setUserProfile(docSnap.data());
                openAlert(`${socialProfile.provider === "kakao" ? "카카오" : "네이버"}로 로그인했어요! 환영합니다 👋`, "로그인 성공 ✨");
            } else {
                setPendingSocialData({
                    email: socialProfile.email,
                    name: socialProfile.name,
                    provider: socialProfile.provider
                });
            }
        } catch (e) {
            console.error("Social Processing Failed", e);
            openAlert("로그인 처리 중 오류가 발생했습니다.", "오류");
        }
    };

    const handleSocialPersistentLogin = async (socialProfile) => {
        let email = socialProfile.email;
        const providerId = socialProfile.provider;
        const socialId = socialProfile.id;

        if (!email) {
            email = `${providerId}_${socialId}@thestaff.app`;
        }
        const password = `TheStaff_${providerId}_${socialId}_Secure!`;

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        } catch (error) {
            if (error.code === "auth/user-not-found" || error.code === "auth/invalid-credential" || error.code === "auth/wrong-password") {
                try {
                    const newUser = await createUserWithEmailAndPassword(auth, email, password);
                    return newUser.user;
                } catch (createError) {
                    console.error("Social Signup Failed:", createError);
                    if (createError.code === "auth/email-already-in-use") {
                        openAlert(`이미 가입된 이메일입니다 (${email}).\n기존에 가입하신 방식(이메일 또는 다른 소셜)으로 로그인해주세요.`, "로그인 실패");
                        throw new Error("Email already in use");
                    }
                    openAlert(`회원가입 처리 중 오류가 발생했습니다: ${createError.message}`, "오류");
                    throw createError;
                }
            }
            console.error("Persistent Login Error:", error);
            openAlert(`로그인 처리 중 오류가 발생했습니다: ${error.message}`, "오류");
            throw error;
        }
    };

    const handleGoogleLogin = async () => {
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Google login failed", error);
            openAlert(`Google 로그인 실패: ${error.message}`, "오류");
        }
    };

    const handleKakaoLogin = async () => {
        try {
            const profile = await loginWithKakao();
            if (profile) {
                const currentUser = await handleSocialPersistentLogin(profile);
                await processSocialLogin(currentUser, profile);
            }
        } catch (e) {
            console.error("Kakao Login Error", e);
            openAlert(`카카오 로그인 실패: ${e.message}`, "오류");
        }
    };

    const handleNaverLogin = async () => {
        try {
            setLoading(true);
            const { popup } = await loginWithNaver();

            const checkPopup = setInterval(() => {
                if (popup && popup.closed) {
                    clearInterval(checkPopup);
                    setTimeout(() => setLoading(false), 1000);
                }
            }, 1000);
        } catch (e) {
            console.error("Naver Login Error", e);
            setLoading(false);
            openAlert(`네이버 로그인 실패: ${e.message}`, "오류");
        }
    };

    const handleSignup = async (email, password, additionalInfo) => {
        const themePayload = buildThemePayload(additionalInfo);

        if (pendingSocialData && user) {
            try {
                await setDoc(doc(db, "users", user.uid), {
                    email: user.email || additionalInfo.email || email,
                    name: additionalInfo.name,
                    phone: additionalInfo.phone,
                    role: additionalInfo.role,
                    churchName: additionalInfo.churchName,
                    deptName: additionalInfo.deptName,
                    ...themePayload,
                    provider: pendingSocialData.provider,
                    createdAt: new Date().toISOString(),
                    isVerified: true
                });

                setUserProfile({ ...additionalInfo, ...themePayload });
                setPendingSocialData(null);
                openAlert("환영합니다! 가입이 완료되었습니다.", "가입 완료");
                seedDemoData(user);
                setAppReady(true);
                return;
            } catch (e) {
                console.error("Social Signup Save Failed", e);
                openAlert(`정보 저장 실패: ${e.message}`, "오류");
                return;
            }
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const newUser = userCredential.user;

            await sendEmailVerification(newUser);
            openAlert("회원가입 성공! \n입력하신 이메일로 인증 메일을 발송했습니다.\n확인 후 로그인해주세요.", "이메일 인증 안내");

            await setDoc(doc(db, "users", newUser.uid), {
                email,
                name: additionalInfo.name,
                phone: additionalInfo.phone,
                role: additionalInfo.role,
                churchName: additionalInfo.churchName,
                deptName: additionalInfo.deptName,
                ...themePayload,
                createdAt: new Date().toISOString(),
                isVerified: false
            });
        } catch (error) {
            console.error("Signup failed", error);
            if (error.code === "auth/email-already-in-use") {
                openAlert("이미 가입된 이메일입니다.", "가입 실패");
            } else {
                openAlert(`회원가입 실패: ${error.message}`, "오류");
            }
            throw error;
        }
    };

    const handleCancelSocialSignup = async () => {
        await signOut(auth);
        setPendingSocialData(null);
        setUser(null);
    };

    return {
        processSocialLogin,
        handleSocialPersistentLogin,
        handleGoogleLogin,
        handleKakaoLogin,
        handleNaverLogin,
        handleSignup,
        handleCancelSocialSignup
    };
}
